// pages/Usage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import API_BASE_URL from '../config/api';

interface UsageService {
  serviceName: string;
  used: number;
  limit: number | null;
  resetDate: string;
  isActive: boolean;
  percentageUsed: number;
  isUnlimited: boolean;
  status: 'normal' | 'warning' | 'critical' | 'exceeded' | 'unlimited';
}

interface CurrentUsage {
  subscription: {
    planType: string;
    status: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
  };
  usage: Record<string, { total: number; unit: string; count: number }>;
  services: Record<string, UsageService>;
  periodStart: string;
  periodEnd: string;
}

interface UsageHistory {
  date: string;
  API_REQUESTS?: number;
  TOKENS_USED?: number;
  MESSAGES_SENT?: number;
}

interface UsageLimits {
  currentPlan: {
    type: string;
    status: string;
    billingCycle: string;
    features: string[];
  };
  limits: UsageService[];
  upgradeSuggestions: Array<{
    plan: string;
    reason: string;
    benefits: string[];
  }>;
  lastUpdated: string;
}

const Usage: React.FC = () => {
  const navigate = useNavigate();
  const [currentUsage, setCurrentUsage] = useState<CurrentUsage | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistory[]>([]);
  const [usageLimits, setUsageLimits] = useState<UsageLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'current' | 'history' | 'limits'>('current');
  const [timeframe, setTimeframe] = useState('7d');

  useEffect(() => {
    loadUsageData();
  }, [activeTab, timeframe]);

  const apiCall = async (url: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return null;
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      navigate('/login');
      return null;
    }

    return response;
  };

  const loadUsageData = async () => {
    try {
      setLoading(true);
      setError('');

      switch (activeTab) {
        case 'current':
          await loadCurrentUsage();
          break;
        case 'history':
          await loadUsageHistory();
          break;
        case 'limits':
          await loadUsageLimits();
          break;
      }
    } catch (err) {
      setError('Failed to load usage data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUsage = async () => {
    const response = await apiCall('/api/usage/current');
    if (!response) return;

    const data = await response.json();
    if (data.success) {
      setCurrentUsage(data.data);
    } else {
      setError(data.message);
    }
  };

  const loadUsageHistory = async () => {
    const response = await apiCall(`/api/usage/history?granularity=daily&limit=30`);
    if (!response) return;

    const data = await response.json();
    if (data.success) {
      setUsageHistory(data.data.history);
    } else {
      setError(data.message);
    }
  };

  const loadUsageLimits = async () => {
    const response = await apiCall('/api/usage/limits');
    if (!response) return;

    const data = await response.json();
    if (data.success) {
      setUsageLimits(data.data);
    } else {
      setError(data.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'exceeded': return 'text-red-800 bg-red-200';
      case 'unlimited': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatNumber = (num: number | null): string => {
    if (num === null) return 'Unlimited';
    return num.toLocaleString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate('/subscription')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
              >
                Manage Subscription
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { key: 'current', label: 'Current Usage' },
              { key: 'history', label: 'Usage History' },
              { key: 'limits', label: 'Plan Limits' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading usage data...</p>
          </div>
        ) : (
          <div>
            {/* Current Usage Tab */}
            {activeTab === 'current' && currentUsage && (
              <div className="space-y-6">
                {/* Subscription Info */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Overview</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Plan</p>
                      <p className="text-lg font-semibold text-gray-900">{currentUsage.subscription.planType}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Status</p>
                      <p className={`text-lg font-semibold ${
                        currentUsage.subscription.status === 'ACTIVE' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {currentUsage.subscription.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Period</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(currentUsage.periodStart)} - {formatDate(currentUsage.periodEnd)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Usage Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(currentUsage.services).map(([serviceName, service]) => (
                    <div key={serviceName} className="bg-white rounded-lg shadow p-6">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900">
                          {serviceName.replace('_', ' ')}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(service.status)}`}>
                          {service.status}
                        </span>
                      </div>
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>{formatNumber(service.used)} used</span>
                          <span>{formatNumber(service.limit)} limit</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              service.percentageUsed > 80 ? 'bg-red-500' :
                              service.percentageUsed > 60 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(service.percentageUsed, 100)}%` }}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Resets on {formatDate(service.resetDate)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage History Tab */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Usage History</h2>
                  <select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-1 text-sm"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </select>
                </div>
                
                {usageHistory.length > 0 ? (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={usageHistory}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="API_REQUESTS" 
                          stroke="#8884d8" 
                          name="API Requests"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="TOKENS_USED" 
                          stroke="#82ca9d" 
                          name="Tokens Used"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="MESSAGES_SENT" 
                          stroke="#ffc658" 
                          name="Messages Sent"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No usage history available for the selected timeframe
                  </div>
                )}
              </div>
            )}

            {/* Plan Limits Tab */}
            {activeTab === 'limits' && usageLimits && (
              <div className="space-y-6">
                {/* Current Plan Info */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Current Plan</p>
                      <p className="text-xl font-bold text-gray-900">{usageLimits.currentPlan.type}</p>
                      <p className="text-sm text-gray-600">{usageLimits.currentPlan.billingCycle} billing</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Features</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {usageLimits.currentPlan.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            {feature}
                          </li>
                        ))}
                        {/* {usageLimits.currentPlan.features.length > 3 && (
                          <li className="text-gray-500">+{usageLimits.currentPlan.features.length - 3} more...</li>
                        )} */}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Limits Breakdown */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage Limits</h2>
                  <div className="space-y-4">
                    {usageLimits.limits.map((limit, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <h3 className="font-medium text-gray-900">
                            {limit.serviceName.replace('_', ' ')}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(limit.status)}`}>
                            {limit.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Used</p>
                            <p className="font-semibold">{formatNumber(limit.used)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Limit</p>
                            <p className="font-semibold">{formatNumber(limit.limit)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Remaining</p>
                            {/* <p className="font-semibold">{formatNumber(limit.remaining)}</p> */}
                          </div>
                          <div>
                            <p className="text-gray-500">Usage</p>
                            <p className="font-semibold">{limit.percentageUsed}%</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upgrade Suggestions */}
                {usageLimits.upgradeSuggestions.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-blue-900 mb-4">Upgrade Suggestions</h2>
                    {usageLimits.upgradeSuggestions.map((suggestion, index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <h3 className="font-medium text-blue-800">Upgrade to {suggestion.plan}</h3>
                        <p className="text-sm text-blue-600 mb-2">{suggestion.reason}</p>
                        <ul className="text-sm text-blue-600 ml-4">
                          {suggestion.benefits.map((benefit, i) => (
                            <li key={i} className="list-disc">{benefit}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <button
                      onClick={() => navigate('/subscription')}
                      className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Usage;